import React, { FC, useState } from 'react';
import { Alert } from '@kontentino/ui';
import { queryKey } from 'constants/queryKey';
import PostApi from 'api/post';
import { getLinkOwnershipTokenFailureReason } from 'app/components/linkAttachments/linkOwnershipFailure';
import SettingUpLinkOwnershipModal from 'app/components/linkAttachments/components/SettingUpLinkOwnershipModal';
import { useQuery } from 'react-query';
import { useTranslation } from 'react-i18next';

type Props = {
  pageId: number;
  url: string;
  onVerifiedChange(isVerified: boolean): void;
};

const LinkOwnershipVerification: FC<Props> = ({
  pageId,
  url,
  onVerifiedChange,
}) => {
  const { t } = useTranslation();
  const [shown, setModalShown] = useState(false);
  const [hidden, setOwnershipHidden] = useState(false);

  const { isLoading, isError, error, data } = useQuery(
    queryKey.linkDomainVerification({ pageId, url: url ?? '' }),
    () =>
      PostApi.checkLinkDomainVerification({
        pageId: pageId,
        url,
      }),
    {
      onSuccess: (res) => {
        onVerifiedChange(res.ownership_permissions.can_customize_link_posts);
      },
      // Both outcomes clear the flag so the card editor stays locked; only the
      // message differs. Keeping a previous `true` here would unlock editing
      // for a newly entered URL whose ownership was never established.
      onError() {
        onVerifiedChange(false);
      },
    },
  );

  function hideOwnership() {
    setOwnershipHidden(true);
  }

  const tokenFailureReason = isError
    ? getLinkOwnershipTokenFailureReason(error)
    : null;
  const isVerified = !!data?.ownership_permissions.can_customize_link_posts;

  if (hidden) {
    return null;
  }

  if (isLoading) {
    return (
      <Alert variant="warning" onClose={hideOwnership}>
        {t('linkOwnershipVerifying')}
      </Alert>
    );
  }

  if (tokenFailureReason) {
    return (
      <Alert variant="warning" onClose={hideOwnership}>
        {t('linkOwnershipUnknownReconnectProfile')}
      </Alert>
    );
  }

  if (isError || !isVerified) {
    return (
      <>
        <Alert
          variant="danger"
          onClose={hideOwnership}
          cta={{
            position: 'right',
            label: t('learnMore'),
            onClick: () => setModalShown(true),
          }}
        >
          {t('linkOwnershipNotVerified')}
        </Alert>
        <SettingUpLinkOwnershipModal
          open={shown}
          onClose={() => setModalShown(false)}
        />
      </>
    );
  }

  return null;
};

export default LinkOwnershipVerification;
